import * as vp from 'vopay';
import { FunctionComponent, JSX, useEffect, useState } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchClientCreditCards from '@/hooks/useApi/useFetchClientCreditCards';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type CreditCardTransactionInfoProps = {
  transaction: vp.CreditCardTransactionResponse;
};

type FC = FunctionComponent<CreditCardTransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
};

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
  cardDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};

const CreditCardTransactionInfo: FC = (props: CreditCardTransactionInfoProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.CreditCardTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { state } = useLocation<DataProps>();
  const { transaction } = props;
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { creditCards, fetchCreditCards } = useFetchClientCreditCards();
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [isGetNonClientControlled, setIsGetNonClientControlled] = useState(false);
  const [creditCardsList, setCreditCardsList] = useState<{
    [idx: number]: vp.ClientCreditCardLists;
  }>({});

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: transaction.TransactionID,
    }).then(() => {
      if (transaction.CreditCardToken !== '' && !isGetNonClientControlled) {
        fetchCreditCards({
          PortalUser: '',
          ClientControlled: true,
          CreditCardToken: transaction.CreditCardToken,
        });

        setIsGetNonClientControlled(true);
      }
    });
  }, [transaction.TransactionID, currentDate]);

  useEffect(() => {
    if (isGetNonClientControlled) {
      fetchCreditCards({
        PortalUser: '',
        ClientControlled: false,
        CreditCardToken: transaction.CreditCardToken,
      });
      setIsGetNonClientControlled(false);
    }
    setCreditCardsList({ ...creditCardsList, ...creditCards });
  }, [creditCards]);

  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetails')} />
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('cardDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('cardDetails')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </div>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetails: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transaction.TransactionID,
          },
          {
            display: t('transactionDetails.transactionStatus'),
            value: (
              <Badge
                label={tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                  defaultValue: transaction.TransactionStatus,
                })}
                hasIcon
                color={vp.GetColorStatus(transaction.TransactionStatus)}
              />
            ),
          },
          {
            display: t('transactionDetails.transactionDateTime'),
            value: transaction.TransactionDateTime,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transaction.LastModified,
          },
          {
            display: t('transactionDetails.type'),
            value: t('creditCard') || '',
          },
          {
            display: t('transactionDetails.amount'),
            value: `$${formattedCurrency(transaction.Amount)}`,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${formattedCurrency(transaction.HoldAmount)}`,
          },
          {
            display: t('transactionDetails.currency'),
            value: transaction.Currency,
          },
          {
            display: t('transactionDetails.notes'),
            value:
              transactions
                .filter((x) => x.TransactionID === transaction.TransactionID)
                .map((data) => data.Notes)
                .pop() || '',
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return accountInfoProps;
      },
      cardDetails: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('cardDetails.name'),
            value:
              creditCardsList[0]?.CreditCardHolderName ||
              transactions
                .filter((x) => x.TransactionID === transaction.TransactionID)
                .map((data) => data.FullName)
                .pop() ||
              '',
            visible: true,
          },
          {
            display: t('cardDetails.cardNumber'),
            value: creditCardsList[0]?.CreditCardNumber || '',
            visible: !!creditCardsList[0]?.CreditCardNumber,
          },
          {
            display: t('cardDetails.brand'),
            value: creditCardsList[0]?.CreditCardBrand || '',
            visible: !!creditCardsList[0]?.CreditCardBrand,
          },
          {
            display: t('cardDetails.expiry'),
            value: `${creditCardsList[0]?.CreditCardExpiryMonth || ''} / ${
              creditCardsList[0]?.CreditCardExpiryYear || ''
            }`,
            visible: !!creditCardsList[0]?.CreditCardExpiryMonth,
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'card-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('cardDetails.header') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountInfoProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transaction.LastModified,
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.header') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default CreditCardTransactionInfo;
